import { useEffect, useState } from 'react';
import { HelpLink } from './DirectorHelp.tsx';
import {
  CLIP_LIMIT_STEP,
  CLIP_POLICIES,
  CLIP_TRASH_MS,
  MAX_CLIP_SECONDS,
  clipFromLabel,
  clipKindLabel,
  formatClipTime,
  formatSpotTime,
  sortApproved,
  type ClientMessage,
  type ClipsState,
  type ClipSenderView,
  type ClipView,
} from '@rabbitfuel/shared';
import { timeShort } from '../lib/format.ts';
import type { AdBreakView } from './Ads.tsx';

/**
 * Botschaften an die Regie: was Athlet:innen und Zuschauer:innen geschickt
 * haben, in drei Faechern.
 *
 * - **Neu** -- ansehen, dann freigeben, als Favorit markieren oder loeschen.
 * - **Freigegeben** -- Favoriten oben, damit die richtige im richtigen Moment
 *   schnell zur Hand ist. „Senden" spielt sie wie einen Werbespot: gross im
 *   Bild, das Live-Bild klein in der Ecke.
 * - **Papierkorb** -- eine Stunde lang zurueckholbar, dann endgueltig weg.
 *
 * Darunter die Absender, um die sich die Regie kuemmern muss: wer die Grenze
 * erreicht hat (Zuschauer:innen duerfen erst einmal drei schicken) oder
 * blockiert ist.
 */

const OPEN_KEY = 'rabbitfuel:deck:clips';

function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) !== '0';
  } catch {
    return true;
  }
}

export function ClipDesk({
  clips,
  ad,
  send,
  onBeforeAir,
}: {
  clips: ClipsState;
  /** Die laufende Pause -- ist es eine Botschaft, steht sie oben mit Restzeit. */
  ad: AdBreakView;
  send: (msg: ClientMessage) => void;
  /** Im Klick auf „Senden" -- dort darf der Browser der Regie Ton freigeben. */
  onBeforeAir?: () => void;
}) {
  const [open, setOpen] = useState(readOpen);
  const [preview, setPreview] = useState<string | null>(null);
  const [showTrash, setShowTrash] = useState(false);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      localStorage.setItem(OPEN_KEY, next ? '1' : '0');
    } catch {
      /* Privater Modus -- dann eben nur bis zum Neuladen. */
    }
  };

  const fresh = clips.clips.filter((c) => c.status === 'pending' || c.status === 'processing');
  const approved = clips.clips.filter((c) => c.status === 'approved').sort(sortApproved);
  const trash = clips.clips.filter((c) => c.status === 'trash');
  const senders = new Map(clips.senders.map((s) => [s.key, s]));
  const attention = clips.senders.filter((s) => s.blocked || s.wantsMore || (s.allowed !== null && s.sent >= s.allowed));
  const onAir = ad.active?.clip ? ad.active : null;

  const decide = (clip: ClipView, action: 'approve' | 'favorite' | 'unfavorite' | 'trash' | 'restore') => {
    /*
     * Mit der Entscheidung ist das Ansehen vorbei. Ausserdem wandert die
     * Botschaft dabei in ein anderes Fach -- die Vorschau wuerde dort neu
     * aufgebaut und finge von vorn an zu spielen, als liefe sie von selbst.
     */
    if (preview === clip.id && action !== 'unfavorite') setPreview(null);
    send({ t: 'clip:decide', clipId: clip.id, action });
  };
  const air = (clip: ClipView) => {
    onBeforeAir?.();
    setPreview(null);
    send({ t: 'clip:air', clipId: clip.id });
  };

  const row = (clip: ClipView) => (
    <ClipRow
      key={clip.id}
      clip={clip}
      sender={senders.get(clip.senderKey)}
      playing={onAir?.clip?.id === clip.id}
      previewing={preview === clip.id}
      onPreview={() => setPreview(preview === clip.id ? null : clip.id)}
      onDecide={(action) => decide(clip, action)}
      onAir={() => air(clip)}
      onBlock={() => send({ t: 'clip:sender', senderKey: clip.senderKey, action: 'block' })}
    />
  );

  return (
    <section className={`deck${open ? '' : ' is-collapsed'}`}>
      <header className="deck__head">
        <button type="button" className="deck__toggle" onClick={toggle} aria-expanded={open}>
          {open ? '▾' : '▸'} Botschaften
        </button>
        <HelpLink topic="botschaften" />
        {onAir ? (
          <span className="deck__mode deck__mode--ad">
            Botschaft läuft <ClipCountdown ad={ad} />
          </span>
        ) : (
          <span className={`deck__mode${fresh.length ? ' deck__mode--new' : ' deck__mode--off'}`}>
            {fresh.length
              ? `${fresh.length} neu`
              : clips.policy === 'off'
                ? 'nimmt keine an'
                : approved.length
                  ? `${approved.length} freigegeben`
                  : 'keine'}
          </span>
        )}
      </header>

      {/* Wie bei der Werbepause: was laeuft, gehoert auch zugeklappt ins Blickfeld. */}
      {onAir?.clip && (
        <div className="ad-running">
          <span>
            <b>{onAir.clip.from}</b> läuft — noch <ClipCountdown ad={ad} />
          </span>
          <button type="button" className="btn btn--sm btn--onstage" onClick={() => send({ t: 'clip:air', clipId: null })}>
            Abbrechen
          </button>
        </div>
      )}

      {open && (
        <div className="deck__body">
          <div className="deck__block">
            <div className="deck__label">Botschaften annehmen von</div>
            <div className="chip-row">
              {CLIP_POLICIES.map((p) => {
                const needsZone = p.id === 'local' && !clips.zone;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`chip${clips.policy === p.id ? ' is-active' : ''}`}
                    disabled={needsZone}
                    title={needsZone ? 'Dafür braucht das Event einen Bereich — in den Event-Einstellungen.' : p.hint}
                    onClick={() => send({ t: 'clip:policy', policy: p.id })}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="deck__hint">
              {CLIP_POLICIES.find((p) => p.id === clips.policy)?.hint}
              {!clips.zone && ' „Nur vor Ort“ geht erst mit einem Event-Bereich (Einstellungen).'}
            </div>
          </div>

          <div className="deck__block">
            <div className="deck__label">
              Neu <span className="deck__count">{fresh.length}</span>
            </div>
            {fresh.length === 0 ? (
              <div className="deck__hint">
                Noch nichts Neues. Athlet:innen und Zuschauer:innen können auf ihrer Seite eine Video- oder
                Sprachnachricht bis {MAX_CLIP_SECONDS} Sekunden schicken — sie landet zuerst hier.
              </div>
            ) : (
              <ul className="clip-list">{fresh.map(row)}</ul>
            )}
          </div>

          <div className="deck__block">
            <div className="deck__label">
              Freigegeben <span className="deck__count">{approved.length}</span>
            </div>
            {approved.length === 0 ? (
              <div className="deck__hint">Freigegebene Botschaften stehen hier, Favoriten ganz oben. „Senden“ spielt sie im Stream.</div>
            ) : (
              <ul className="clip-list">{approved.map(row)}</ul>
            )}
          </div>

          {attention.length > 0 && (
            <div className="deck__block">
              <div className="deck__label">
                Absender <span className="deck__count">{attention.length}</span>
              </div>
              <ul className="clip-senders">
                {attention.map((s) => (
                  <SenderRow key={s.key} sender={s} send={send} />
                ))}
              </ul>
            </div>
          )}

          <div className="deck__block">
            <button type="button" className="deck__link" onClick={() => setShowTrash((v) => !v)}>
              {showTrash ? '▾' : '▸'} Papierkorb ({trash.length})
            </button>
            {showTrash &&
              (trash.length === 0 ? (
                <div className="deck__hint">Leer. Gelöschte Botschaften liegen hier eine Stunde, dann sind sie endgültig weg.</div>
              ) : (
                <ul className="clip-list">{trash.map(row)}</ul>
              ))}
          </div>
        </div>
      )}
    </section>
  );
}

function ClipRow({
  clip,
  sender,
  playing,
  previewing,
  onPreview,
  onDecide,
  onAir,
  onBlock,
}: {
  clip: ClipView;
  sender: ClipSenderView | undefined;
  playing: boolean;
  previewing: boolean;
  onPreview: () => void;
  onDecide: (action: 'approve' | 'favorite' | 'unfavorite' | 'trash' | 'restore') => void;
  onAir: () => void;
  onBlock: () => void;
}) {
  const ready = clip.status !== 'processing' && Boolean(clip.url);
  return (
    <li className={`clip-row is-${clip.status}${clip.favorite ? ' is-favorite' : ''}${playing ? ' is-playing' : ''}`}>
      <div className="clip-row__main">
        <span className="clip-row__icon" aria-hidden>
          {clip.kind === 'video' ? '🎥' : '🎤'}
        </span>
        <div className="clip-row__who">
          <b>{clipFromLabel(clip.from)}</b>
          <span>
            {clipKindLabel[clip.kind]}
            {clip.durationMs > 0 ? ` · ${formatClipTime(clip.durationMs)}` : ''} · {timeShort.format(new Date(clip.createdAt))}
            {sender?.allowed != null ? ` · ${sender.sent}/${sender.allowed}` : ''}
            {clip.airedAt ? ` · gesendet ${timeShort.format(new Date(clip.airedAt))}` : ''}
          </span>
        </div>
        <div className="clip-row__actions">
          {clip.status === 'processing' ? (
            <span className="deck__hint">wird aufbereitet …</span>
          ) : clip.status === 'trash' ? (
            <>
              <TrashLeft trashedAt={clip.trashedAt} />
              <button type="button" className="btn btn--sm btn--onstage" onClick={() => onDecide('restore')}>
                Wiederherstellen
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className={`clip-star${clip.favorite ? ' is-on' : ''}`}
                aria-pressed={clip.favorite}
                title={clip.favorite ? 'Favorit — nach oben gestellt' : 'Als Favorit markieren (gibt auch frei)'}
                onClick={() => onDecide(clip.favorite ? 'unfavorite' : 'favorite')}
              >
                {clip.favorite ? '★' : '☆'}
              </button>
              <button type="button" className="btn btn--sm btn--onstage" disabled={!ready} onClick={onPreview}>
                {previewing ? 'Schließen' : 'Ansehen'}
              </button>
              {clip.status === 'pending' ? (
                <button type="button" className="btn btn--sm" onClick={() => onDecide('approve')}>
                  Freigeben
                </button>
              ) : (
                <button type="button" className="btn btn--sm" disabled={!ready || playing} onClick={onAir}>
                  {playing ? 'läuft' : '▶ Senden'}
                </button>
              )}
              {/* Rot und mit Wort statt Symbol: man soll sehen, dass hier etwas weggeht. */}
              <button
                type="button"
                className="btn btn--sm clip-delete"
                title="In den Papierkorb — eine Stunde lang zurückholbar"
                onClick={() => onDecide('trash')}
              >
                Löschen
              </button>
            </>
          )}
        </div>
      </div>
      {previewing && clip.url && (
        <div className="clip-row__preview">
          {clip.kind === 'video' ? (
            <video src={clip.url} controls autoPlay playsInline />
          ) : (
            <audio src={clip.url} controls autoPlay />
          )}
          <span className="deck__hint">
            Vorschau nur hier in der Regie. Im Stream läuft die Botschaft erst mit „▶ Senden“.
          </span>
          {sender && !sender.blocked && clip.from.role !== 'organizer' && (
            <button type="button" className="deck__link" onClick={onBlock}>
              Absender blockieren — von {sender.name} kommt dann nichts mehr an
            </button>
          )}
        </div>
      )}
    </li>
  );
}

function SenderRow({ sender, send }: { sender: ClipSenderView; send: (msg: ClientMessage) => void }) {
  return (
    <li className="clip-sender">
      <div className="clip-row__who">
        <b>{sender.name}</b>
        <span>
          {sender.allowed !== null ? `${sender.sent} von ${sender.allowed} Botschaften` : `${sender.sent} Botschaften`}
          {sender.blocked ? ' · blockiert' : sender.wantsMore ? ' · möchte weitere schicken' : ''}
        </span>
      </div>
      <div className="clip-row__actions">
        {!sender.blocked && sender.allowed !== null && (
          <button type="button" className="btn btn--sm" onClick={() => send({ t: 'clip:sender', senderKey: sender.key, action: 'more' })}>
            +{CLIP_LIMIT_STEP} erlauben
          </button>
        )}
        <button
          type="button"
          className="btn btn--sm btn--onstage"
          onClick={() => send({ t: 'clip:sender', senderKey: sender.key, action: sender.blocked ? 'unblock' : 'block' })}
        >
          {sender.blocked ? 'Entsperren' : 'Blockieren'}
        </button>
      </div>
    </li>
  );
}

/** „noch 43 min" bis zum endgueltigen Loeschen -- tickt einmal pro Minute. */
function TrashLeft({ trashedAt }: { trashedAt: number | null }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  if (trashedAt === null) return null;
  const minutes = Math.max(0, Math.ceil((trashedAt + CLIP_TRASH_MS - Date.now()) / 60_000));
  return <span className="deck__hint">noch {minutes} min</span>;
}

function ClipCountdown({ ad }: { ad: AdBreakView }) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!ad.active) return;
    const timer = window.setInterval(() => tick((n) => n + 1), 500);
    return () => window.clearInterval(timer);
  }, [ad.active]);
  if (!ad.active) return null;
  return <span className="ad-countdown">{formatSpotTime(ad.active.durationMs - ad.position())}</span>;
}
